import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { FiEye, FiTrash2, FiLink } from 'react-icons/fi';
import { fetchScreenResults, deleteScreenResult } from '../../lib/api/screenResults';
import { createShareLink } from '../../lib/api/shareLinks';

const ScreenResultsViewer = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sharingId, setSharingId] = useState(null);
  const [shareFeedback, setShareFeedback] = useState(null);

  const loadResults = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchScreenResults();
      setResults(data);
    } catch (err) {
      setError(err.message || 'Failed to load screen results');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResults();
  }, []);

  const handleDelete = async (resultId) => {
    if (!window.confirm('Delete this screen result?')) return;
    const removedIndex = results.findIndex((result) => result.id === resultId);
    const removedResult = removedIndex >= 0 ? results[removedIndex] : null;
    setError('');
    setResults((prev) => prev.filter((result) => result.id !== resultId));
    try {
      await deleteScreenResult(resultId);
    } catch (err) {
      setResults((prev) => {
        if (!removedResult || prev.some((result) => result.id === resultId)) {
          return prev;
        }
        const next = prev.slice();
        const insertIndex = Math.min(removedIndex, next.length);
        next.splice(insertIndex, 0, removedResult);
        return next;
      });
      setError(err.message || 'Failed to delete screen result');
    }
  };

  const handleShare = async (resultId) => {
    setShareFeedback(null);
    try {
      setSharingId(resultId);
      const shareId = await createShareLink('screen_result', resultId);
      const shareUrl = `${window.location.origin}/share/${shareId}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        window.prompt('Copy share link:', shareUrl);
      }
      setShareFeedback({ id: resultId, message: 'Share link copied to clipboard.' });
    } catch (err) {
      setShareFeedback({
        id: resultId,
        message: err.message || 'Failed to create share link',
        isError: true
      });
    } finally {
      setSharingId(null);
    }
  };

  const renderSummary = (screenResult) => {
    const data = screenResult?.screen_results || {};
    const elements = Array.isArray(data.elements) ? data.elements : [];
    if (elements.length > 0) {
      const selected = elements
        .flatMap((el) => (el.results || []).filter((row) => row.selected))
        .map((row) => row.ticker)
        .filter(Boolean);
      const selectedCount = elements.reduce((acc, el) => acc + (el.selected_count || 0), 0);
      const totalUniverse = elements.reduce((acc, el) => acc + (el.total_universe || 0), 0);
      return {
        screenDate: data.screen_date || elements[0]?.screen_date,
        selectedCount,
        totalUniverse,
        topTickers: selected.slice(0, 8)
      };
    }

    const selected = (data.results || []).filter((row) => row.selected).map((row) => row.ticker).filter(Boolean);
    return {
      screenDate: data.screen_date,
      selectedCount: data.selected_count,
      totalUniverse: data.total_universe,
      topTickers: selected.slice(0, 8)
    };
  };

  return (
    <div className="screen-results-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Screen Results</h1>
          <p className="text-gray-600">Review stored screening runs and open them in the builder.</p>
        </div>
        <Button onClick={() => navigate('/dashboard/strategies')} className="flex items-center gap-2">
          Run New Screen
        </Button>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Loading screen results...</div>
      ) : (
        <div className="space-y-4">
          {results.map((result) => {
            const summary = renderSummary(result);
            return (
              <Card key={result.id} className="border border-gray-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold">{result.name}</CardTitle>
                    <p className="text-xs text-gray-500">
                      Created {new Date(result.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/dashboard/strategies?screen_result=${result.id}`)}
                      className="flex items-center gap-1"
                    >
                      <FiEye />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleShare(result.id)}
                      className="flex items-center gap-1"
                      disabled={sharingId === result.id}
                    >
                      <FiLink />
                      {sharingId === result.id ? 'Sharing...' : 'Share'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(result.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <FiTrash2 />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <div className="text-xs text-gray-500">Screen Date</div>
                      <div className="text-sm font-medium text-gray-900">{summary.screenDate || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Selected</div>
                      <div className="text-sm font-medium text-gray-900">
                        {summary.selectedCount ?? 'N/A'} / {summary.totalUniverse ?? 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Top Tickers</div>
                      <div className="text-sm text-gray-900">
                        {summary.topTickers.length > 0 ? summary.topTickers.join(', ') : 'N/A'}
                      </div>
                    </div>
                  </div>
                  {shareFeedback?.id === result.id && (
                    <div className={`mt-3 text-xs ${shareFeedback.isError ? 'text-red-600' : 'text-emerald-600'}`}>
                      {shareFeedback.message}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {results.length === 0 && (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-gray-500">
              No screen results yet. Run a screen to see results here.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScreenResultsViewer;
